<template>
    <div class="handoff-notification-card">
        <div class="card-header">
            <v-icon class="header-icon" small>mdi-bell-ring-outline</v-icon>
            <span class="header-text">Don't want to wait?</span>
        </div>

        <p class="card-description">
            We'll text you at <strong>{{ phone_number }}</strong> as soon as a team member responds. No need to keep this page open!
        </p>

        <div class="card-actions">
            <button
                class="action-btn primary"
                :class="{ 'is-confirmed': notification_confirmed }"
                :disabled="notification_confirmed"
                @click="handleNotifyMe"
            >
                <v-icon color="white" small>{{ notification_confirmed ? 'mdi-check' : 'mdi-message-text-outline' }}</v-icon>
                <span>{{ notification_confirmed ? 'We\'ll text you!' : 'Text me when ready' }}</span>
            </button>
        </div>

        <div v-if="show_app_promo" class="app-promo-section">
            <div class="promo-divider">
                <span>or</span>
            </div>

            <div class="app-promo-content">
                <img
                    src="@/assets/redchirp-square-large.png"
                    alt="RedChirp App"
                    @error="handleImageError"
                />
                <div v-if="image_error" class="app-icon-fallback">
                    <v-icon color="white">mdi-glass-wine</v-icon>
                </div>
                <div class="promo-text">
                    <p class="promo-headline">Get the RedChirp App for instant notifications</p>
                    <p class="promo-subtext">Plus early allocation access & shipment tracking</p>
                </div>
            </div>

            <button class="app-download-btn" @click="$emit('download-app')">
                <svg class="apple-logo" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                <span>Download the App</span>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HandoffNotificationCard',

    props: {
        phone_number: {
            type: String,
            default: 'your phone'
        },
        show_app_promo: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            image_error: false,
            notification_confirmed: false
        };
    },

    methods: {
        handleImageError() {
            this.image_error = true;
        },

        handleNotifyMe() {
            this.notification_confirmed = true;
            this.$emit('notify-me');
        },
    },
};
</script>

<style lang="scss" scoped>
.handoff-notification-card {
    background: var(--wine-bg);
    border-radius: 1rem;
    padding: 1rem;
    border: 1px solid #e8e8e8;
    transition: all 0.2s ease;
}

.header {
    &-icon {
        color: var(--brand-primary) !important;
    }

    &-text {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--brand-primary-dark);
    }
}

.card {
    &-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    &-description {
        font-size: 0.8rem;
        color: #666;
        line-height: 1.4;
        margin: 0 0 1rem 0;

        strong {
            color: #333;
        }
    }
    
    &-actions {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &.primary {
        background: var(--brand-primary);
        color: white;

        &:hover:not(:disabled) {
            background: var(--brand-primary-dark);
        }

        &.is-confirmed {
            background: var(--v-success-darken1);
            color: white !important;
            cursor: default;
        }
    }

    &.secondary {
        background: transparent;
        color: #666;

        &:hover {
            background: rgba(0, 0, 0, 0.05);
            color: #333;
        }
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }
}

.app-promo {
    &-section {
        margin-top: 0.5rem;
        padding-top: 1rem;
    }

    &-content {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        margin-bottom: 0.75rem;

        img {
            width: 3rem;
            height: 3rem;
            object-fit: contain;
        }
    }
}

.promo {
    &-divider {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1rem;

        &::before,
        &::after {
            content: '';
            flex: 1;
            height: 1px;
            background: #ddd;
        }

        span {
            font-size: 0.75rem;
            color: #999;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
    }

    &-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-dark) 100%);
        border-radius: 0.5rem;
        flex-shrink: 0;
    }

    &-text {
        flex: 1;
    }

    &-headline {
        font-size: 0.85rem;
        font-weight: 600;
        margin: 0 0 0.125rem 0;
    }

    &-subtext {
        font-size: 0.75rem;
        margin: 0;
    }
}

.app-download-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.65rem 1rem;
    background: #000;
    border: none;
    border-radius: 0.5rem;
    color: white;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #333;
    }

    &:active {
        transform: scale(0.98);
    }
}

.app-icon-fallback {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-dark) 100%);
}

.apple-logo {
    width: 1rem;
    height: 1rem;
}
</style>
